// The list at the top of a change-set review when commit is blocked: each
// item that still needs a decision, in plain words, with Go to that scrolls
// to the row and opens it (SAD-901). Counted from the same wire flags zou's
// commit gate reads — open/routed items and unresolved Blocking rows — never
// a second heuristic.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSectionHeader, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { blockingItemPlain, isOpenReviewItem, isReviewItem, NO_TARGET_NAMES, needsDecisionNote, noWriterNeedsDecision, SETTLED_STATES } from './reviewItemCopy';
const BLOCKING_LIST_ID = 'blocking-commit-list';
// Items zou's validateCommitInvariants still refuses to commit: Open/Routed,
// unresolved Blocking (Escalated, or Rejected on a review item), and no-writer
// rows that still need Leave blank.
export function itemsBlockingCommit(items) {
    return items.filter((d)=>{
        const settled = SETTLED_STATES.has(d.resolutionState) || d.resolutionState === 'Rejected' && !isReviewItem(d);
        return !settled || noWriterNeedsDecision(d);
    });
}
export function scrollToBlockingList() {
    document.getElementById(BLOCKING_LIST_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
    });
}
function scrollToAndOpen(id, onOpen) {
    const row = document.querySelector(`[data-testid="${Testids.reviewItem.row}"][data-item-id="${id}"]`) || document.querySelector(`[data-item-id="${id}"]`);
    row?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
    });
    onOpen(id);
}
export function BlockingCommitList({ items, periods = [], targetNames = NO_TARGET_NAMES, onOpen }) {
    const blocking = itemsBlockingCommit(items);
    if (blocking.length === 0) return null;
    // Prefer open review items for the plain-language cards; fall back to every
    // blocking row so the count on the list matches the commit button.
    const listed = blocking.some(isOpenReviewItem) ? blocking.filter(isOpenReviewItem) : blocking;
    return /*#__PURE__*/ _jsxs("section", {
        id: BLOCKING_LIST_ID,
        "data-testid": Testids.reviewItem.blockingList,
        "data-count": blocking.length,
        className: "scroll-mt-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: `${needsDecisionNote(blocking.length)} before you can commit`
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                className: "mt-[var(--space-1-5)]",
                children: "Each one below still needs your call. Go to opens it."
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "mt-2 space-y-2",
                children: listed.map((d)=>{
                    const plain = blockingItemPlain(d, periods, targetNames);
                    const review = isReviewItem(d) ? d.review : undefined;
                    return /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs(SpecraSurface, {
                            radius: "md",
                            className: "p-3",
                            "data-testid": Testids.reviewItem.blockingItem,
                            "data-item-id": d.id,
                            "data-reason-code": review?.reasonCode,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-start justify-between gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            size: "body-ui",
                                            className: "font-medium text-foreground",
                                            children: plain.title
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            type: "button",
                                            size: "sm",
                                            variant: "secondary",
                                            onClick: ()=>scrollToAndOpen(d.id, onOpen),
                                            "data-testid": Testids.reviewItem.blockingGoTo,
                                            "data-item-id": d.id,
                                            children: "Go to"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-2 space-y-1 text-[length:var(--fs-ui-body)]",
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "span",
                                                    size: "meta",
                                                    tone: "muted",
                                                    children: [
                                                        "Document says:",
                                                        ' '
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    as: "span",
                                                    size: "body-ui",
                                                    children: plain.documentSays
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "span",
                                                    size: "meta",
                                                    tone: "muted",
                                                    children: [
                                                        "Conflicts with:",
                                                        ' '
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    as: "span",
                                                    size: "body-ui",
                                                    children: plain.conflictsWith
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "span",
                                                    size: "meta",
                                                    tone: "muted",
                                                    children: [
                                                        "Choose:",
                                                        ' '
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    as: "span",
                                                    size: "body-ui",
                                                    className: "font-medium",
                                                    children: plain.choose
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    }, d.id);
                })
            }),
            listed.length < blocking.length && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                className: "mt-2",
                children: [
                    "Plus ",
                    blocking.length - listed.length,
                    " more change item",
                    blocking.length - listed.length === 1 ? '' : 's',
                    " in the table below."
                ]
            })
        ]
    });
}
